import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import PasswordInput from "@/components/ui/PasswordInput";
import { getUniqueUsername } from "@/utils/username.native";
import auth from "@react-native-firebase/auth";
import firestore from "@react-native-firebase/firestore";
import {
  GoogleSignin,
  GoogleSigninButton,
} from "@react-native-google-signin/google-signin";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Alert,
  Linking,
  Platform,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { getErrorMessage } from "../../utils/errors";

const privacyUrl =
  process.env.EXPO_PUBLIC_PRIVACY_POLICY_URL ||
  (Platform.OS === "web"
    ? "/privacy-policy.html"
    : "https://vehilink.app/privacy-policy.html");
const termsUrl =
  process.env.EXPO_PUBLIC_TERMS_URL ||
  (Platform.OS === "web" ? "/terms.html" : "https://vehilink.app/terms.html");

const SignupScreen = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const router = useRouter();
  const { t } = useTranslation();

  const handleSignup = async () => {
    if (!email || !password) {
      Alert.alert(t("ERROR"), t("PLEASE_ENTER_EMAIL"));
      return;
    }
    setAuthLoading(true);
    try {
      const userCredential = await auth().createUserWithEmailAndPassword(
        email,
        password,
      );

      const user = userCredential.user;
      const generatedUsername = await getUniqueUsername(
        firestore(),
        user.email?.split("@")[0] || "user",
      );

      await firestore()
        .collection("users")
        .doc(user.uid)
        .set({
          uid: user.uid,
          email: user.email,
          displayName: user.email,
          username: generatedUsername,
          createdAt: firestore.FieldValue.serverTimestamp(),
          providers: user.providerData.map((p) => p.providerId),
          termsAccepted: true,
          recipientMonthlyWAQuota: 1,
          notificationSettings: { globalFeedPosts: true },
        });

      await userCredential.user.sendEmailVerification();
      await auth().signOut();

      Alert.alert(t("VERIFICATION_SENT"), t("VERIFICATION_EMAIL_SENT"));

      router.replace("/(auth)/login");
    } catch (error) {
      Alert.alert(t("ERROR"), getErrorMessage(error));
    } finally {
      setAuthLoading(false);
    }
  };

  const onGoogleButtonPress = async () => {
    setAuthLoading(true);
    try {
      await GoogleSignin.hasPlayServices({
        showPlayServicesUpdateDialog: true,
      });
      await GoogleSignin.signOut();
      const signInResult = await GoogleSignin.signIn();

      // runtime-safe narrowing for different library shapes
      const anyResult = signInResult as any;
      const idToken = anyResult?.data?.idToken ?? anyResult?.idToken;
      const googleEmail =
        anyResult?.data?.user?.email ?? anyResult?.user?.email;

      if (!idToken || !googleEmail) {
        throw new Error(t("GOOGLE_SIGNIN_FAILED"));
      }

      const methods = await auth().fetchSignInMethodsForEmail(googleEmail);

      if (methods.length > 0) {
        Alert.alert(t("ACCOUNT_EXISTS"), t("ACCOUNT_EXISTS_MESSAGE"));
        return;
      }

      const googleCredential = auth.GoogleAuthProvider.credential(idToken);
      const userCredential = await auth().signInWithCredential(googleCredential);
      const user = userCredential.user;
      const userDocRef = firestore().collection("users").doc(user.uid);
      const userDoc = await userDocRef.get();

      if (!userDoc.exists) {
        const generatedUsername = await getUniqueUsername(
          firestore(),
          user.displayName || user.email?.split("@")[0] || "user",
        );

        await userDocRef.set({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          username: generatedUsername,
          photoURL: user.photoURL,
          createdAt: firestore.FieldValue.serverTimestamp(),
          providers: user.providerData.map((p) => p.providerId),
          termsAccepted: true,
          recipientMonthlyWAQuota: 1,
          notificationSettings: { globalFeedPosts: true },
        });
      }

      router.replace("/(auth)/onboarding" as any);
    } catch (error) {
      Alert.alert(t("ERROR"), getErrorMessage(error));
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <SafeAreaView
      style={[styles.safeAreaViewContainer, { backgroundColor: "#eee" }]}
      edges={["top", "bottom"]}
    >
      <StatusBar style="dark" animated={true} />

      <ThemedView style={styles.container}>
        {authLoading ? (
          <ActivityIndicator size="large" color="#4285F4" />
        ) : (
          <>
            <ThemedText style={styles.title} type="title">
              {t("SIGN_UP")}
            </ThemedText>
            <TextInput
              style={styles.input}
              placeholder={t("EMAIL")}
              placeholderTextColor="#888"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
            />
            <PasswordInput value={password} onChangeText={setPassword} />
            <Button
              onPress={handleSignup}
              disabled={!email || !password || authLoading}
              text={t("SIGN_UP")}
            />
            <ThemedView style={styles.dividerContainer}>
              <ThemedView style={styles.divider} />
              <ThemedText style={styles.orText}>{t("OR")}</ThemedText>
              <ThemedView style={styles.divider} />
            </ThemedView>

            <GoogleSigninButton
              size={GoogleSigninButton.Size.Wide}
              style={styles.googleButton}
              color={GoogleSigninButton.Color.Dark}
              onPress={onGoogleButtonPress}
              disabled={authLoading}
            />

            <ThemedText style={styles.consentText}>
              {t("BY_SIGNING_UP_AGREE")}{" "}
              <ThemedText
                style={styles.link}
                onPress={() => Linking.openURL(termsUrl)}
              >
                {t("TERMS_AND_CONDITIONS")}
              </ThemedText>{" "}
              {t("AND")}{" "}
              <ThemedText
                style={styles.link}
                onPress={() => Linking.openURL(privacyUrl)}
              >
                {t("PRIVACY_POLICY")}
              </ThemedText>
              .
            </ThemedText>

            <TouchableOpacity
              style={styles.signupButton}
              onPress={() => router.replace("/(auth)/login")}
            >
              <ThemedText style={styles.signupText}>
                {t("ALREADY_HAVE_ACCOUNT")} {t("LOGIN")}
              </ThemedText>
            </TouchableOpacity>
          </>
        )}
      </ThemedView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeAreaViewContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    backgroundColor: "#fff",
  },
  title: {
    marginBottom: 32,
  },
  input: {
    width: "100%",
    maxWidth: 350,
    height: 48,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    marginBottom: 16,
    fontSize: 16,
    backgroundColor: "#fafafa",
    color: "#000",
  },
  dividerContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 16,
    marginBottom: 0,
  },
  divider: {
    flex: 1,
    height: 1,
    backgroundColor: "#ccc",
    marginHorizontal: 8,
  },
  orText: {
    fontSize: 16,
  },
  googleButton: {
    marginTop: 5,
    paddingVertical: 12,
    paddingHorizontal: 30,
    borderRadius: 8,
    width: "100%",
    maxWidth: 350,
    alignItems: "center",
  },
  consentText: {
    fontSize: 12,
    color: "#666",
    textAlign: "center",
    marginTop: 12,
    maxWidth: 350,
  },
  link: {
    fontSize: 12,
    color: "#007AFF",
    textDecorationLine: "underline",
  },
  signupButton: {
    marginTop: 24,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  signupText: {
    color: "#007AFF",
    fontSize: 16,
    textAlign: "center",
  },
});

export default SignupScreen;
